import { IconButton } from '@material-ui/core'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Modal from '@material-ui/core/Modal'
import Select from '@material-ui/core/Select'
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import CancelIcon from '@material-ui/icons/Cancel'
import Alert from '@material-ui/lab/Alert'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import TheCart from '../../../../../common/js/api/cart'
import * as helpers from './helpers'
import { choose, IRegion, IRegionWithId, parseUnits } from './helpers'
import { useRegion } from './hook'

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    title: {
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
    icon: {
      cursor: 'pointer',
      width: 12,
      maxHeight: 12,
      marginTop: 1,
      marginLeft: theme.spacing(1),
    },
    modal: {
      position: 'absolute',
      backgroundColor: 'white',
      top: '10%',
      left: 'calc(70% / 2)',
      width: '30%',
      height: 'auto',
      borderRadius: '10px',

      '& p': {
        padding: theme.spacing(2),
      },
    },
    mobileModal: {
      top: 0,
      left: 0,
      borderRadius: 0,
      // height: '100vh',
      width: '100%',
    },
    modalInner: {
      padding: theme.spacing(2),
    },
    formControl: {
      margin: theme.spacing(1),
      width: '100%',
      paddingRight: theme.spacing(2),
    },
    centered: {
      textAlign: 'center',
    },
    centeredButtons: {
      textAlign: 'center',
      margin: '0 auto',
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    regionIcon: {
      marginTop: -13,
    },
    regionAvatar: {
      height: 24,
      width: 24,
      display: 'inline-block',
      marginRight: theme.spacing(1),
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
    },
    warning: {
      margin: theme.spacing(3),
    },
  })
})

interface IProps {
  isOpen?: boolean
  onClose?: (selectedRegion?: IRegion) => void
}

const makeId = (countryCode: string, currencyCode: string) =>
  `${countryCode}-${currencyCode}`

const currencyRegions = (regions: IRegion[]) =>
  regions.reduce((acc: IRegionWithId[], r) => {
    return acc.concat(
      r.currencyCodes.map((cc) => {
        return {
          ...r,
          id: makeId(r.countryCode, cc.code),
          currencyCode: cc.code,
        }
      })
    )
  }, [])

const RegionChooser = (props: IProps) => {
  const cart = TheCart()
  const { t, ready: tReady, i18n } = useTranslation('RegionChooser', {
    useSuspense: false,
  })
  const region = useRegion()

  const classes = useStyles()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(props.isOpen)
  const [regions, setRegions] = useState<IRegionWithId[]>([])

  const [selectedRegion, setSelectedRegion] = useState<IRegionWithId>(null)
  const [countryCode, setCountryCode] = useState(region.shipToCountryCode)
  const [units, setUnits] = useState(helpers.unparseUnits(region.units))
  const [currencyCode, setCurrencyCode] = useState(region.currencyCode)
  const [nextLink, setNextLink] = useState('/')
  const [cartHasItems, setCartHasItems] = useState(false)

  useEffect(() => {
    if (!region.ready) {
      return
    }

    const { geoCountryCode, allRegions } = region

    if (allRegions.length === 0) {
      return
    }

    setRegions(currencyRegions(allRegions))

    const chosenRegion = choose(
      allRegions,
      window.location.pathname,
      geoCountryCode,
      navigator.languages.join(';')
    )
    const foundRegion =
      allRegions.find((r) => r.countryCode === chosenRegion.defaultTo) ||
      allRegions[0]

    setSelectedRegion(currencyRegions([foundRegion])[0])
    setCountryCode(foundRegion.countryCode)
    setCurrencyCode(foundRegion.currencyCodes[0].code)
    setUnits(foundRegion.units)
    i18n.changeLanguage(foundRegion.languageCode)
  }, [region.ready])

  useEffect(() => {
    if (!selectedRegion) {
      setNextLink('/')
      return
    }

    const hreflang = selectedRegion.hreflang
    const selector = `head > link[rel="alternate"][hreflang="${hreflang}"]`
    const alternate = document.querySelector(selector)

    if (alternate && alternate.getAttribute('href')) {
      setNextLink(alternate.getAttribute('href'))
      return
    }

    setNextLink(selectedRegion.path)
  }, [selectedRegion])

  useEffect(() => {
    cart
      .getId()
      .then((id) => {
        if (id) {
          return cart.get(id).then((resp) => {
            setCartHasItems(resp.data.line_items.physical_items.length > 0)
          })
        }
      })
      .catch((err) => {
        console.error(err)
      })
  }, [])

  if (!tReady) {
    return null
  }

  const handleOnClose = (selectedRegion?: IRegionWithId) => {
    if (selectedRegion) {
      helpers.setUnits(parseUnits(units))
      helpers.setCurrencyCode(currencyCode)
      helpers.setShipToCountryCode(selectedRegion.countryCode)
      helpers.setTax(selectedRegion.tax)
      helpers.setCartPath(selectedRegion.cartPath)
      helpers.setPath(selectedRegion.path)

      if (region.currencyCode !== selectedRegion.currencyCode) {
        cart.resetId()
      }
    }

    helpers.setRegionChooserDismissed(true)

    if (props.onClose) {
      props.onClose()
    }
    setModalIsOpen(false)

    if (selectedRegion) {
      if (nextLink.startsWith('/')) {
        window.location.pathname = nextLink
        return
      }
      window.location.href = nextLink
    }

    return true
  }

  const body: any = (
    <div className={clsx([classes.modal, { [classes.mobileModal]: isMobile }])}>
      <IconButton
        className={classes.closeButton}
        onClick={() => handleOnClose()}
      >
        <CancelIcon />
      </IconButton>
      <div className={classes.modalInner}>
        <h4 className={clsx(classes.title, classes.centered)}>{t('title')}</h4>
        <form>
          <FormControl className={classes.formControl}>
            <InputLabel shrink id="demo-simple-select-placeholder-label-label">
              {t('language-and-currency')}
            </InputLabel>
            <Select
              labelId="demo-simple-select-placeholder-label-label"
              id="demo-simple-select-placeholder-label"
              value={makeId(countryCode, currencyCode)}
              onChange={(evt) => {
                const newRegion = regions.find(
                  (cr) => cr.id === evt.target.value
                )

                setSelectedRegion(newRegion)
                setCurrencyCode(newRegion.currencyCode)
                setUnits(newRegion.units)
                setCountryCode(newRegion.countryCode)
                i18n.changeLanguage(newRegion.languageCode)
              }}
              displayEmpty
              className={classes.selectEmpty}
            >
              {regions.map((region) => {
                return (
                  <MenuItem key={region.id} value={region.id}>
                    <Avatar
                      className={classes.regionAvatar}
                      src={region.flagImage}
                    />
                    {region.countryName} ({region.currencyCode})
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel shrink id="demo-simple-select-placeholder-label-label">
              {t('measurement-system')}
            </InputLabel>
            <Select
              labelId="demo-simple-select-placeholder-label-label"
              id="demo-simple-select-placeholder-label"
              value={units}
              onChange={(evt) => {
                setUnits(evt.target.value as string)
              }}
              displayEmpty
              className={classes.selectEmpty}
            >
              <MenuItem value="metric">
                <em>{t('metric')}</em>
              </MenuItem>
              <MenuItem value="imperial">{t('imperial')}</MenuItem>
            </Select>
          </FormControl>
          {region.currencyCode === null ||
          region.currencyCode === currencyCode ||
          !cartHasItems ? null : (
            <Alert className={classes.warning} severity="warning">
              {t('change-currency-empty-cart')}
            </Alert>
          )}
          <div className={clsx(classes.formControl, classes.centeredButtons)}>
            <Button
              variant="contained"
              color="primary"
              href={nextLink}
              onClick={(evt) => {
                evt.preventDefault()
                handleOnClose(selectedRegion)
              }}
            >
              {t('choose-button')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )

  return (
    <div>
      <Modal open={modalIsOpen} onClose={() => handleOnClose()}>
        {body}
      </Modal>
    </div>
  )
}

export default RegionChooser
